import * as React from 'react';

import { DisplayDate, formatDay } from 'owa-datetime';

import CalendarAllDaySurfaceItem from './surfaceItems/CalendarAllDaySurfaceItem';
import CalendarConflictGroupItem from './surfaceItems/CalendarConflictGroupItem';
import CalendarDateHeader from './surfaceItems/CalendarDateHeader';
import type CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import CalendarOverflowIndicator from './surfaceItems/CalendarOverflowIndicator';
import CalendarSurfaceBlankItem from './surfaceItems/CalendarSurfaceBlankItem';
import CalendarSurfaceItem from './surfaceItems/CalendarSurfaceItem';
import type CalendarSurfaceStore from './store/schema/CalendarSurfaceStore';
import type { ColorScheme } from 'owa-color-utils/lib/ColorScheme';
import type { ConflictGroup } from './interfaces/ConflictGroup';
import { ContentHeader } from './surfaceItems/ContentHeader';
import type { TemplateFunc } from './interfaces/SurfaceItem';
import getBaseTimeZone from './utils/getBaseTimeZone';

/**
 * This file defines templates we are exposing to consumers
 * Use SurfaceTemplates.tsx for internal templates not exposed to consumer
 */

/**
 * Default column template for week view
 * @param item: surface item
 * @param content: getter function for providing group content
 */
export let conflictGroupTemplate: TemplateFunc = (
    conflictGroup: ConflictGroup,
    content: () => JSX.Element[]
): JSX.Element => {
    return <CalendarConflictGroupItem key={conflictGroup.key} content={content} />;
};

/**
 * Default in-day item template
 * @param item: surface item
 * @param content: getter function for providing content
 * @param store: calendar surface store to pass user options
 */
export let inDayItemTemplate = (
    store: CalendarSurfaceStore,
    model: CalendarEvent,
    colorScheme: ColorScheme,
    charmId: number,
    state?: any,
    increaseFontSize?: boolean,
    joinButtonVisible?: boolean
): JSX.Element => {
    return (
        <CalendarSurfaceItem
            key={model.ItemId.Id}
            item={model}
            colorScheme={colorScheme}
            charmId={charmId}
            disableTabIndex={store.disableTabbableSurfaceItems}
            itemState={{
                continuesLeft: false,
                continuesRight: false,
                timeZoneId: getBaseTimeZone(store.timeZoneIds),
            }}
            increaseFontSize={increaseFontSize}
            joinButtonVisible={joinButtonVisible}
        />
    );
};

/**
 * Default all-day item template
 * @param item: surface item
 * @param content: getter function for providing content
 */
export let allDayItemTemplate = (
    store: CalendarSurfaceStore,
    model: CalendarEvent,
    colorScheme: ColorScheme,
    charmId: number,
    itemState?: any,
    increaseFontSize?: boolean,
    joinButtonVisible?: boolean
): JSX.Element => {
    return (
        <CalendarAllDaySurfaceItem
            key={model.ItemId.Id}
            item={model}
            colorScheme={colorScheme}
            charmId={charmId}
            disableTabIndex={store.disableTabbableSurfaceItems}
            itemState={itemState}
            increaseFontSize={increaseFontSize}
            joinButtonVisible={joinButtonVisible}
        />
    );
};

/**
 * Default blank item template
 * @param item: surface item
 * @param content: getter function for providing content
 */
export let blankItemTemplate = (
    store: CalendarSurfaceStore,
    model: CalendarEvent,
    colorScheme: ColorScheme,
    itemState?: any
): JSX.Element => {
    return (
        <CalendarSurfaceBlankItem
            key={model.ItemId.Id}
            freeBusyType={model.FreeBusyType}
            colorScheme={colorScheme}
            disableTabIndex={store.disableTabbableSurfaceItems}
            hideFreeBusyBar={false}
        />
    );
};

/**
 * Default template for date header
 * @param key: any unique key for react
 * @param displayDate: date header model
 */
export function createDateColumnHeader(
    displayDate: DisplayDate,
    key: number,
    style: React.CSSProperties,
    renderDateHeaderAddOn?: (date: DisplayDate) => JSX.Element,
    onSurfaceClick?: (
        start: DisplayDate,
        end: DisplayDate,
        isAllDay: boolean,
        props?: any,
        selectedCalendarId?: string
    ) => void,
    calendarFolderId?: string,
    shouldShowWeekMonthName?: () => boolean,
    hideTodayEmphasis?: boolean
) {
    return (
        <CalendarDateHeader
            key={key}
            displayDate={displayDate}
            formatHeader={formatDay}
            hideTodayEmphasis={hideTodayEmphasis}
            onSurfaceClick={onSurfaceClick}
            calendarFolderId={calendarFolderId}
            style={style}
            data-tabid={'surfaceHeader_' + key}
            shouldShowWeekMonthName={shouldShowWeekMonthName}>
            {!!renderDateHeaderAddOn && renderDateHeaderAddOn(displayDate)}
        </CalendarDateHeader>
    );
}

export let contentHeaderTemplate: (
    key: string,
    text: string,
    colorScheme: ColorScheme,
    onClick: () => void
) => JSX.Element = (key, text, colorScheme, onClick) => {
    return (
        <ContentHeader
            key={key}
            text={text}
            colorScheme={colorScheme}
            onClick={onClick}
            data-tabId={'contentHeader_' + key}
        />
    );
};

export let freeBusyTemplate: (
    key: string,
    className: string,
    colorScheme: ColorScheme
) => JSX.Element = (key, className, colorScheme) => {
    let style: React.CSSProperties = {
        backgroundColor: colorScheme.calPrimary,
    };
    return <div key={key} style={style} className={className} />;
};

/**
 * Template for overflow item
 */
export let overflowItemTemplate: (
    store: CalendarSurfaceStore,
    model: CalendarEvent
) => JSX.Element = (store, model) => {
    return <CalendarOverflowIndicator key={model.ItemId.Id} item={model} />;
};
